import profileApi from 'services/api/profile';
import utilityApi from 'services/api/utility';
import Actions from 'Actions';
import store from 'store/Store';
import addressConstants from 'constants/Address';

import analyticsAddress from 'analytics/bindingMethods/pages/myAccount/addressPageBindings';
import anaConsts from 'analytics/constants';
import addressUtils from 'utils/Address';
import localeUtils from 'utils/LanguageLocale';
import userActions from './UserActions';
import Location from 'utils/Location';
import { refreshCheckout } from 'services/Checkout';
import * as addressFingerprintUtils from 'utils/addressFingerprint';
import { buildAvsCustomPayload, formatRecommendedAddress } from 'utils/avsCustomPayload';

const { updateLocalDefaultShippingAddressData } = addressUtils;
const { rememberDecision, recordSoftBlockShown, getSoftBlockShownCount, SOFT_BLOCK_PROMPT_CAP } = addressFingerprintUtils;
const SHIPPING_ADDRESS_TYPE = 'S';

export default {
    getSavedAddresses: function (profileId, successCallback) {
        return profileApi.getShippingAddresses(profileId).then(data => successCallback(data.addressList));
    },

    setDefaultAddress: function (addressId, profileId, successCallback) {
        return profileApi
            .setDefaultShippingAddress(addressId)
            .then(() => profileApi.getShippingAddresses(profileId))
            .then(data => successCallback(data.addressList));
    },

    updateDefaultShippingAddressData: function (addressList) {
        const isCheckout = Location.isCheckout();
        const isReplacementOrderPage = Location.isReplacementOrderPage();

        const defaultAddress = Array.isArray(addressList)
            ? addressList.find(address => address.isDefault && (isCheckout || isReplacementOrderPage || address.typeCode === SHIPPING_ADDRESS_TYPE))
            : null;

        if (defaultAddress) {
            const { country: defaultSACountryCode, postalCode: defaultSAZipCode } = defaultAddress;

            // Update the default shipping address in local storage
            updateLocalDefaultShippingAddressData({
                defaultSACountryCode,
                defaultSAZipCode
            });

            // Update the default shipping address in the store
            store.dispatch(
                userActions.updateStoreDefaultShippingAddressData({
                    defaultSACountryCode,
                    defaultSAZipCode
                })
            );
        }
    },

    deleteAddress: function (addressId, profileId, successCallback, failureCallback) {
        profileApi.removeShippingAddress(profileId, addressId).then(successCallback).catch(failureCallback);
    },

    addNewAddress: function (addressData, successCallback, failureCallback) {
        profileApi.addShippingAddress(addressData).then(successCallback).catch(failureCallback);
    },

    updateAddress: function (addressData, successCallback, failureCallback) {
        return profileApi.updateShippingAddress(addressData).then(successCallback).catch(failureCallback);
    },

    getStateList: function (countryCode, successCallback) {
        utilityApi.getStateList(countryCode).then(successCallback);
    },

    getShippingCountriesList: function (successCallback) {
        utilityApi.getShippingCountryList().then(successCallback);
    },

    validateAddress: async function (
        addressData,
        successCallbackObj = {},
        cancelCallback,
        pageType = digitalData.page.category.pageType,
        shouldRefreshCheckout = false,
        isReleaseThreeVerifyAddressModal = false
    ) {
        const {
            address1, address2 = '', state, city, postalCode, country, source
        } = addressData;
        const addressConstantsTexts = addressConstants(isReleaseThreeVerifyAddressModal);

        const avsLoqateV2 = Sephora.configurationSettings?.avsLoqateV2;

        // Legacy request body — built identically to the pre-AVS-v2 code so that with the flag OFF
        // the request payload is byte-for-byte unchanged. All v2-only fields are added inside the
        // avsLoqateV2 branch below.
        const dataToSend = {
            // according to API address1 param should include address1 and address2
            address1: `${address1} ${address2}`.trim(),
            state,
            city,
            postalCode,
            country
        };

        if (avsLoqateV2) {
            const { ADDRESS_DECISION, ADDRESS_VERIFICATION_TYPE } = addressConstantsTexts;

            // GIM-5450: AVS Loqate v2 additive request fields — added ONLY when the flag is on. `channel`
            // is always 'UFE' (the schema's web channel); `locale` lets the service resolve copy;
            // `cartContext.isHazmat` lets it apply hazmat restrictions. Field names match the AVS GraphQL
            // AddressValidationInput (CartContextInput.isHazmat). address1/address2 are coerced to '' so a
            // null part never stringifies to the literal "null" in the concatenation below.
            // GIM-5456 (E2-7): `source` is deliberately NOT sent — AddressValidationInput has no `source`
            // field, so it is a UFE-internal routing flag only (see isPayPalSource below).
            const hasHazmat = (store.getState().basket?.items || []).some(item => item?.sku?.isHazmat === true);
            dataToSend.address1 = `${address1 || ''} ${address2 || ''}`.trim();
            dataToSend.channel = 'UFE';
            dataToSend.locale = localeUtils.getCurrentLanguageCountryCode();
            dataToSend.cartContext = { isHazmat: hasHazmat };

            // GIM-5456 (E2-7): PayPal-supplied addresses run the SAME 5-state contract but can't be edited
            // field-by-field inside the PayPal sheet — so SUGGESTED is suppressed (treated as proceed) and a
            // block routes to the manual address form (UNVERIFIED), with PayPal still selectable as payment.
            // `source` is a UFE-internal routing flag: it is NOT sent to AVS, and it is stripped from the
            // address before it flows to any downstream consumer (see proceed()).
            const isPayPalSource = source === 'paypal';
            const routeToManualEntry = () => {
                const unverifiedCallback = successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED];
                unverifiedCallback && unverifiedCallback();
            };

            // ACCEPTED / BYPASSED proceed with the entered address; the legacy GOOD branch
            // proceeded via successCallbackObj[RECOMMENDED], so reuse it here. BYPASSED passes an
            // optional override address (stored with addressValidated:false).
            const proceed = overrideAddress => {
                const successCallback = successCallbackObj[ADDRESS_VERIFICATION_TYPE.RECOMMENDED];

                if (!successCallback) {
                    return;
                }

                const address = overrideAddress !== undefined ? overrideAddress : addressData;

                // GIM-5456: strip the UFE-internal `source` flag ONLY when present (PayPal), so it never
                // reaches the RECOMMENDED callback / shippingAddress update API. Non-PayPal flows pass the
                // original object through unchanged — preserving referential identity (no clone / extra
                // renders on this hot path).
                if (address && address.source !== undefined) {
                    const { source: _omitSource, ...addressWithoutSource } = address;
                    successCallback(addressWithoutSource);
                } else {
                    successCallback(address);
                }
            };

            const handleDecision = data => {
                // GIM-5455 (E2-6): remember the server's verdict keyed by the address fingerprint so the
                // resubmit/re-edit rules can decide when to re-prompt vs skip. No client decision logic —
                // we only store the server decision verbatim.
                rememberDecision(addressData, data.decision);

                switch (data.decision) {
                    case ADDRESS_DECISION.ACCEPTED: {
                        proceed();

                        break;
                    }
                    case ADDRESS_DECISION.BYPASSED: {
                        // BYPASSED address must be stored with addressValidated=false so callers
                        // know it was not AVS-verified.
                        proceed({ ...addressData, addressValidated: false });

                        break;
                    }
                    case ADDRESS_DECISION.SUGGESTED:
                    case ADDRESS_DECISION.SOFT_BLOCK:
                    case ADDRESS_DECISION.HARD_BLOCK: {
                        // GIM-5456 (E2-7): PayPal SUGGESTED is suppressed — there is no editable form to
                        // pre-fill mid-PayPal — so treat it as proceed (no modal).
                        if (isPayPalSource && data.decision === ADDRESS_DECISION.SUGGESTED) {
                            proceed();

                            break;
                        }

                        // GIM-5455 (E2-6): SOFT_BLOCK re-prompt is capped at once (2 modals total: initial
                        // + 1 re-prompt). On the 3rd identical submission of the same soft-blocked address,
                        // proceed without a 3rd modal instead of nagging the shopper again. The cap does NOT
                        // apply to PayPal (E2-7) — a PayPal block always routes to manual entry.
                        if (
                            !isPayPalSource &&
                            data.decision === ADDRESS_DECISION.SOFT_BLOCK &&
                            getSoftBlockShownCount(addressData) >= SOFT_BLOCK_PROMPT_CAP
                        ) {
                            proceed();

                            break;
                        }

                        shouldRefreshCheckout && refreshCheckout()();
                        // AVS ValidationResponse carries no isOverridable field — override-ability is
                        // implied by the decision (only SOFT_BLOCK may proceed as entered; HARD_BLOCK
                        // and SUGGESTED may not).
                        const isOverridable = data.decision === ADDRESS_DECISION.SOFT_BLOCK;
                        const recommendedAddress = data.recommendedAddress ? Object.assign({}, data.recommendedAddress, { country }) : null;
                        // The current modal still keys off verificationType (render-by-decision lands in
                        // GIM-5451); map the decision so a flag-on block never dispatches an undefined
                        // verificationType, which would crash the legacy modal.
                        const verificationType =
                            data.decision === ADDRESS_DECISION.SUGGESTED
                                ? ADDRESS_VERIFICATION_TYPE.RECOMMENDED
                                : ADDRESS_VERIFICATION_TYPE.UNVERIFIED;

                        store.dispatch(
                            Actions.showAddressVerificationModal({
                                isOpen: true,
                                verificationType,
                                currentAddress: addressData,
                                // Server fields rendered verbatim — UFE applies no decision/copy logic.
                                decision: data.decision,
                                message: data.message,
                                highlightedFields: data.highlightedFields,
                                isOverridable,
                                recommendedAddress,
                                successCallback: () => {
                                    if (isPayPalSource) {
                                        // GIM-5456 (E2-7): PayPal block — route to the manual address form
                                        // (PayPal stays selectable as payment); never proceed as entered. No
                                        // 'entered' analytics here — the shopper is NOT using the address as
                                        // entered (SOT analytics for this route are GIM-5457, out of scope).
                                        routeToManualEntry();
                                    } else if (recommendedAddress) {
                                        // SUGGESTED "Use Recommended" — pre-fill with the recommended address.
                                        successCallbackObj[ADDRESS_VERIFICATION_TYPE.RECOMMENDED](recommendedAddress);
                                        // GIM-5668 (E2-9 [SOT]): tag "use recommended" with the AVS attributes (userOverride=false).
                                        analyticsAddress.handleAnalyticCallback(
                                            'recommended',
                                            buildAvsCustomPayload({
                                                pageType,
                                                addressValidated: data.addressValidated,
                                                recommended: !!data.recommendedAddress,
                                                userOverride: false,
                                                recommendedAddress: formatRecommendedAddress(data.recommendedAddress),
                                                auxiliaryAttributes: data.auxiliaryAttributes
                                            })
                                        );
                                    } else {
                                        // SOFT/HARD block: the primary button is "Edit Address" — open the edit
                                        // form (legacy UNVERIFIED semantics), never proceed as entered.
                                        successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED] &&
                                            successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED](data.highlightedFields);
                                    }
                                },
                                cancelCallback: () => {
                                    if (isPayPalSource) {
                                        // GIM-5456 (E2-7): closing a PayPal block modal also lands on manual entry
                                        // (no 'entered' analytics — the shopper is not using the address as entered).
                                        routeToManualEntry();
                                    } else if (recommendedAddress || isOverridable) {
                                        // "Use the address as entered" override — allowed only for SUGGESTED
                                        // (has a recommendedAddress) and SOFT_BLOCK (isOverridable). HARD_BLOCK
                                        // has NO override, so route it to the edit form instead.
                                        cancelCallback();
                                        // GIM-5668 (E2-9 [SOT]): tag "use entered" with the AVS attributes (userOverride=true).
                                        analyticsAddress.handleAnalyticCallback(
                                            'entered',
                                            buildAvsCustomPayload({
                                                pageType,
                                                addressValidated: data.addressValidated,
                                                recommended: !!data.recommendedAddress,
                                                userOverride: true,
                                                recommendedAddress: formatRecommendedAddress(data.recommendedAddress),
                                                auxiliaryAttributes: data.auxiliaryAttributes
                                            })
                                        );
                                    } else {
                                        successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED] &&
                                            successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED](data.highlightedFields);
                                    }
                                },
                                // GIM-5638: SUGGESTED "Edit Address" reopens the form as entered with the
                                // changed fields highlighted — never pre-filled with the recommended address.
                                editAddressCallback: () => {
                                    successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED] &&
                                        successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED](data.highlightedFields);
                                },
                                isReleaseThreeVerifyAddressModal
                            })
                        );

                        // GIM-5455 (E2-6): record that a SOFT_BLOCK modal was shown for this fingerprint so
                        // the re-prompt cap above trips on the 3rd identical resubmit. Not tracked for PayPal
                        // (E2-7) since PayPal blocks route to manual entry rather than re-prompting.
                        if (!isPayPalSource && data.decision === ADDRESS_DECISION.SOFT_BLOCK) {
                            recordSoftBlockShown(addressData);
                        }

                        // Preserve the legacy modal-open analytics (ASYNC_PAGE_LOAD) on the v2 path:
                        // SUGGESTED (has a recommendedAddress) → SHIPPING_RECOMMENDED, otherwise SHIPPING_UNVERIFIED.
                        analyticsAddress.handleAnalyticAsyncLoad(
                            pageType,
                            recommendedAddress ? anaConsts.PAGE_DETAIL.SHIPPING_RECOMMENDED : anaConsts.PAGE_DETAIL.SHIPPING_UNVERIFIED
                        );

                        break;
                    }
                    default: {
                        // Unknown/missing decision — fail open (non-blocking) like BYPASSED.
                        proceed();
                    }
                }
            };

            try {
                // GIM-5598: the SDN/AVS response nests the 5-state verdict under `validationResponse`
                // ({ matchedAddresses, validationResponse: { decision, message, highlightedFields,
                // recommendedAddress, addressValidated, auxiliaryAttributes } }). Unwrap it so
                // handleDecision reads the real fields. Fall back to the full `response` (in case a
                // caller/service ever returns the verdict flat at the top level) and finally to `{}`
                // (no decision → fail-open proceed() via the default case, matching service-error behavior).
                const response = await utilityApi.validateAddress(dataToSend);
                handleDecision(response?.validationResponse || response || {});
            } catch {
                // BYPASSED proceed: a service error/timeout must NOT block checkout.
                proceed();
            }

            return;
        }

        const handleValidationResponse = data => {
            const { ADDRESS_VERIFICATION_TYPE, ADDRESS_VERIFICATION_LEVEL } = addressConstantsTexts;
            const recommendedAddress = data.matchedAddresses[0];
            const verificationType =
                recommendedAddress.addressVerificationLevel === ADDRESS_VERIFICATION_LEVEL.POOR
                    ? ADDRESS_VERIFICATION_TYPE.UNVERIFIED
                    : ADDRESS_VERIFICATION_TYPE.RECOMMENDED;
            const isRecommended = verificationType === ADDRESS_VERIFICATION_TYPE.RECOMMENDED;
            const successCallback = successCallbackObj[verificationType];
            const pageDetail = isRecommended ? anaConsts.PAGE_DETAIL.SHIPPING_RECOMMENDED : anaConsts.PAGE_DETAIL.SHIPPING_UNVERIFIED;

            recommendedAddress.country = country;

            if (recommendedAddress.addressVerificationLevel === ADDRESS_VERIFICATION_LEVEL.GOOD) {
                // Dont show modals and call success callback with entered address if verification level is good
                successCallback(addressData);
            } else {
                shouldRefreshCheckout && refreshCheckout()();
                // show recommended address modal or unverified address modal based on verification level if NOT good
                store.dispatch(
                    Actions.showAddressVerificationModal({
                        isOpen: true,
                        verificationType: verificationType,
                        currentAddress: addressData,
                        // include country in modal for displaying purposes because recommendedAddress doesn't have it
                        recommendedAddress: isRecommended && Object.assign({}, recommendedAddress, { country }),
                        successCallback: () => {
                            if (isRecommended) {
                                successCallback(recommendedAddress);
                                analyticsAddress.handleAnalyticCallback('recommended');
                            } else {
                                successCallback();
                            }
                        },
                        cancelCallback: () => {
                            cancelCallback();
                            analyticsAddress.handleAnalyticCallback('entered');
                        },
                        isReleaseThreeVerifyAddressModal
                    })
                );

                analyticsAddress.handleAnalyticAsyncLoad(pageType, pageDetail);
            }
        };

        const handleValidationError = reason => {
            // show unverified address modal
            const { ADDRESS_VERIFICATION_TYPE } = addressConstantsTexts;
            const successCallback = successCallbackObj[ADDRESS_VERIFICATION_TYPE.UNVERIFIED];
            const pageDetail = anaConsts.PAGE_DETAIL.SHIPPING_UNVERIFIED;

            if (reason?.errorCode !== -1 || reason.key === 'util.address.loqate.validate.fail') {
                // UTS-3792
                store.dispatch(
                    Actions.showAddressVerificationModal({
                        isOpen: true,
                        verificationType: ADDRESS_VERIFICATION_TYPE.UNVERIFIED,
                        currentAddress: addressData,
                        successCallback,
                        cancelCallback: () => {
                            cancelCallback();
                            analyticsAddress.handleAnalyticCallback('entered');
                        },
                        isReleaseThreeVerifyAddressModal
                    })
                );

                if (pageType === anaConsts.PAGE_TYPES.CHECKOUT || pageType === anaConsts.PAGE_TYPES.REPLACEMENT_ORDER) {
                    analyticsAddress.handleAnalyticAsyncLoad(pageType, pageDetail);
                }
            } else {
                cancelCallback();
            }
        };

        try {
            handleValidationResponse(await utilityApi.validateAddress(dataToSend));
        } catch (reason) {
            handleValidationError(reason);
        }
    }
};
